// Angular
import {
  ChangeDetectorRef,
  OnDestroy,
  Pipe,
  PipeTransform
} from '@angular/core';

// Third party
import { Subscription } from 'rxjs';

// Local library
import { TranslateParams, TranslationService } from '../services/translation.service';

/**
 * Resolve a language key against the active pack, keeping the English copy as
 * the fallback: {{ 'account.preferences.language' | translate: 'Language' }}
 */
@Pipe({
  name: 'translate',
  standalone: true,
  // Impure so copy re-renders as soon as the user switches language
  pure: false
})
export class TranslatePipe implements PipeTransform, OnDestroy {
  private readonly subscription: Subscription;

  private key: string | null = null;
  private fallback = '';
  private stamp = '';
  private value = '';

  constructor(
    private readonly translationService: TranslationService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    this.subscription = this.translationService.language$.subscribe(() => {
      this.key = null;
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Resolve a key, reusing the last result until the inputs or language change
   * @param {string} key Language key to resolve
   * @param {string} fallback Copy to use when the key is missing
   * @param {TranslateParams} params Values for the {name} tokens in the copy
   * @returns {string} Translated copy
   */
  transform(key: string, fallback = '', params?: TranslateParams): string {
    const stamp = params ? JSON.stringify(params) : '';
    if (key === this.key && fallback === this.fallback && stamp === this.stamp)
      return this.value;

    this.key = key;
    this.fallback = fallback;
    this.stamp = stamp;
    this.value = this.translationService.translate(key, fallback, params);

    return this.value;
  }
}
